import { ArrowRightLeft, Layers, ChevronRight, LucideIcon } from 'lucide-react';
import { useAppStore } from 'store';
import { SCREEN_TYPES } from 'utils/constants';
import { ScreenType } from 'types';
import { cn } from 'utils/classnames';

interface Mode {
  id: string;
  label: string;
  description: string;
  icon: LucideIcon;
  subItems: string[];
  screen: string;
  iconBg: string;
  iconColor: string;
  tagBg: string;
  sectionLabel?: string;
}

const modes: Mode[] = [
  {
    id: 'move',
    label: 'Move',
    description: 'Transfer inventory between locations',
    icon: ArrowRightLeft,
    subItems: ['pallet'],
    screen: SCREEN_TYPES.MOVE_SCAN,
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    tagBg: 'bg-blue-50 text-blue-700',
  },
  {
    id: 'pallet-build',
    label: 'Pallet Build',
    description: 'Create cases, assign products, build & stage pallets',
    icon: Layers,
    subItems: ['create case', 'build pallet', 'weigh & close'],
    screen: SCREEN_TYPES.PALLET_BUILD,
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    tagBg: 'bg-emerald-50 text-emerald-700',
    sectionLabel: 'Pallet Management',
  },
];

export const ModeSelectPage = () => {
  const { setCurrentScreen } = useAppStore();

  return (
    <div className="flex flex-col gap-4 py-4">
      <div className="mb-2 px-1">
        <h1 className="text-2xl font-bold text-slate-800">Select Mode</h1>
        <p className="text-sm text-slate-400">Choose your operation type to get started</p>
      </div>

      {modes.map(
        ({
          id,
          label,
          description,
          icon: Icon,
          subItems,
          screen,
          iconBg,
          iconColor,
          tagBg,
          sectionLabel,
        }) => (
          <div key={id}>
            {sectionLabel && (
              <div className="mb-3 flex items-center gap-3">
                <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">
                  {sectionLabel}
                </span>
                <div className="flex-1 border-t border-slate-200" />
              </div>
            )}
            <button
              onClick={() => setCurrentScreen(screen as ScreenType)}
              className="flex w-full items-center gap-4 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm transition-all hover:shadow-md active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            >
              <div
                className={cn(
                  'flex h-12 w-12 shrink-0 items-center justify-center rounded-xl',
                  iconBg
                )}
              >
                <Icon className={cn('h-6 w-6', iconColor)} />
              </div>

              <div className="flex flex-1 flex-col items-start gap-1.5">
                <span className="text-base font-bold text-slate-800">{label}</span>
                <span className="text-xs text-left text-slate-400">{description}</span>
                <div className="flex flex-wrap gap-1.5">
                  {subItems.map((item) => (
                    <span
                      key={item}
                      className={cn('rounded-full px-2.5 py-0.5 text-xs font-medium', tagBg)}
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>

              <ChevronRight className="h-5 w-5 shrink-0 text-slate-300" />
            </button>
          </div>
        )
      )}
    </div>
  );
};
